import { type ActiveSession, type SessionRow, type WorkoutSummary, api } from '../lib/api.js';
import { el, mount, toast } from '../lib/dom.js';
import { formatDateTime } from '../lib/format.js';
import { confirmDialog } from '../lib/modal.js';
import { navigate } from '../lib/router.js';
import { renderNav } from './nav.js';

const WEEKDAY_PLAN: Record<number, 'A' | 'B' | 'R'> = {
  0: 'R', // dom
  1: 'A', // seg
  2: 'B', // ter
  3: 'R', // qua
  4: 'A', // qui
  5: 'B', // sex
  6: 'R', // sáb
};

const DAY_LABEL = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

export async function renderHome() {
  const [{ workouts }, { sessions }, active] = await Promise.all([
    api.get<{ workouts: WorkoutSummary[] }>('/workouts'),
    api.get<{ sessions: SessionRow[] }>('/history?limit=3'),
    api.get<{ sessions: ActiveSession[] }>('/sessions/active').catch(() => ({ sessions: [] })),
  ]);
  const activeSession = active.sessions[0] ?? null;

  const today = new Date();
  const plan = WEEKDAY_PLAN[today.getDay()] ?? 'R';
  const dayLabel = `${DAY_LABEL[today.getDay()]}, ${String(today.getDate()).padStart(2, '0')}/${String(today.getMonth() + 1).padStart(2, '0')}`;

  const startBtn = (code: 'A' | 'B', isToday: boolean) => {
    const w = workouts.find((x) => x.code === code);
    if (!w) return el('div');
    return el(
      'button',
      {
        class: `btn ${isToday ? 'btn--primary' : ''} btn--lg`,
        onclick: async () => {
          try {
            const { session } = await api.post<{ session: { id: number } }>('/sessions', {
              workout_code: code,
            });
            navigate(`/session/${session.id}?code=${code}`);
          } catch {
            toast('Não conseguiu iniciar. Tenta de novo?');
          }
        },
      },
      [`Iniciar Treino ${code}`],
    );
  };

  const recent = sessions.length
    ? el(
        'ul',
        { class: 'stack stack--sm' },
        sessions.map((s) =>
          el('li', {}, [
            el('a', { class: 'card row row--between', href: `#/history/${s.id}` }, [
              el('div', {}, [
                el('div', { class: 'h3' }, [`Treino ${s.workout_code}`]),
                el('div', { class: 'muted', style: 'font-size: var(--text-sm)' }, [
                  formatDateTime(s.started_at),
                  ' · ',
                  `${s.set_count} sets`,
                ]),
              ]),
              el('span', { class: 'dim' }, ['›']),
            ]),
          ]),
        ),
      )
    : el('p', { class: 'dim' }, ['Sem sessões ainda. Bora começar?']);

  mount(
    el(
      'main',
      { class: 'view' },
      [
        el(
          'header',
          {
            class: 'row row--between',
            style: 'margin-bottom: var(--space-5); align-items: flex-start',
          },
          [
            el('div', { class: 'stack stack--sm' }, [
              el('span', { class: 'pill' }, [dayLabel]),
              el('h1', { class: 'h1' }, [
                plan === 'R' ? 'Hoje é descanso' : `Hoje é Treino ${plan}`,
              ]),
              plan !== 'R'
                ? el('p', { class: 'muted' }, ['Bora?'])
                : el('p', { class: 'muted' }, ['Mas se quiser, manda ver mesmo assim.']),
            ]),
            el(
              'a',
              {
                class: 'btn btn--ghost',
                href: '#/about',
                'aria-label': 'Sobre',
                style: 'padding: 0 var(--space-3)',
              },
              ['⋯'],
            ),
          ],
        ),

        activeSession ? renderActiveBanner(activeSession) : null,

        el('section', { class: 'stack stack--md' }, [
          startBtn('A', plan === 'A'),
          startBtn('B', plan === 'B'),
        ]),

        el('section', { style: 'margin-top: var(--space-8)' }, [
          el('h2', { class: 'h3', style: 'margin-bottom: var(--space-3)' }, ['Sessões recentes']),
          recent,
        ]),

        renderNav('home'),
      ].filter(Boolean) as Node[],
    ),
  );
}

function renderActiveBanner(s: ActiveSession): HTMLElement {
  return el(
    'section',
    {
      class: 'card',
      style:
        'border-color: var(--accent-ring); background: var(--accent-soft); margin-bottom: var(--space-4)',
    },
    [
      el('div', { class: 'row row--between' }, [
        el('div', {}, [
          el('div', { class: 'field__label', style: 'color: var(--accent)' }, [
            'Treino em andamento',
          ]),
          el('div', { style: 'font-weight: 600; margin-top: var(--space-1)' }, [
            `Treino ${s.workout_code} · ${s.set_count} set${s.set_count === 1 ? '' : 's'} logado${s.set_count === 1 ? '' : 's'}`,
          ]),
        ]),
      ]),
      el('div', { class: 'row', style: 'gap: var(--space-2); margin-top: var(--space-3)' }, [
        el(
          'button',
          {
            class: 'btn btn--primary',
            type: 'button',
            style: 'flex: 1',
            onclick: () => navigate(`/session/${s.id}?code=${s.workout_code}`),
          },
          ['Continuar'],
        ),
        el(
          'button',
          {
            class: 'btn',
            type: 'button',
            onclick: async () => {
              const ok = await confirmDialog({
                title: 'Descartar treino?',
                message:
                  s.set_count > 0
                    ? `Você tem ${s.set_count} séries logadas. Vai perder todas.`
                    : 'Nada foi logado ainda, então não vai perder nada.',
                okLabel: 'Descartar',
                danger: true,
              });
              if (!ok) return;
              try {
                await api.del(`/sessions/${s.id}`);
                void renderHome();
              } catch {
                toast('Erro ao descartar');
              }
            },
          },
          ['Descartar'],
        ),
      ]),
    ],
  );
}
